import React, { useRef, useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { Icon } from '../../design-system/icons/Icon';
import { Link } from '../Link';
import { CompanyName } from '../CompanyName';
import { useDragToScroll } from '../../hooks/useDragToScroll';
import { usePushIndexUiState } from '../../hooks/usePushIndexUiState';
import { useSearchSort } from '../../contexts/SearchContext';
import { formatFilterLabel, REMOTE_VALUES, HYBRID_VALUES } from '../../utils/facets/format-filter-label';
import { getKnownSingleValueFilterLabel } from '../../utils/facets/single-value-filter-label';
import { shouldIncludeFacetInTopFilters, TOP_FILTER_ATTRIBUTE_ORDER, getTopFilterColorClass, type TopFilterMode } from '../../layouts/helpers/topFilterConfig';
import { supportsOnlyFilterMode } from '../../utils/typesense/array-facet-count-fields';
import { useNewLocationSystemEnabled } from '../../utils/facets/new-location-system-dev-flag';

const EXCLUDED_FROM_ADDITIONAL_FACETS = new Set(['lastSeenMin']);
import { Logo } from '../navigation/Logo';
import type { IntlShape } from 'react-intl';

export interface FacetItem {
  attribute: string;
  value: string;
  label: string;
  count: number;
  isActive: boolean;
  isExcluded: boolean;
  /** "Only this value" (exact-match) state, only ever true for array facets (ARRAY_FACET_COUNT_FIELD). */
  isOnly: boolean;
  colorClass: string;
  icon?: React.ReactNode;
  locationGroup?: 'finland' | 'remote';
}

function facetMode(isExcluded: boolean, isOnly: boolean): TopFilterMode {
  return isExcluded ? 'exclude' : isOnly ? 'only' : 'include';
}

export interface TopFiltersProps {
  intl: IntlShape;
  formatMessage: IntlShape['formatMessage'];
  facetsData: Record<string, Array<{ value: string; label: string; count: number }>>;
  filtersObj: Record<string, string[]>;
  query?: string;
  className?: string;
  /** When provided, shows the icon logo on the left on small screens (scroll row) */
  clearSearch?: (event?: React.MouseEvent<HTMLAnchorElement>) => Promise<void>;
}

type TopFilterRenderItem =
  | { kind: 'bracket'; char: '[' | ']'; key: string }
  | { kind: 'connector'; text: 'AND' | 'OR'; key: string }
  | { kind: 'facet'; facet: FacetItem; key: string };

/**
 * Top Filters component - horizontally scrolling row of filter pills
 * Shows active filters first, then inactive filters
 * Supports virtualization for performance
 */
export const TopFilters: React.FC<TopFiltersProps> = ({
  intl,
  formatMessage,
  facetsData,
  filtersObj,
  query,
  className = '',
  clearSearch,
}) => {
  const { pushIndexUiState } = usePushIndexUiState();
  const { sort } = useSearchSort();
  const listContainerRef = useRef<HTMLDivElement | null>(null);
  // Enable drag-to-scroll
  const dragToScroll = useDragToScroll(true, listContainerRef);

  // TEC-482: which facet attribute the location pills read from, kept in sync with
  // LocationFilterSection's own branch (FilterBuildingBlocks.tsx) so a regionTags selection
  // made in the Advanced Filters modal actually shows up here, instead of this row silently
  // staying on the old `location` facet regardless of the dev cookie.
  const newLocationSystemEnabled = useNewLocationSystemEnabled();
  const locationAttribute = newLocationSystemEnabled ? 'regionTags' : 'location';
  const locationExcludeKey = `exclude_${locationAttribute}`;

  // Helper function for case-insensitive facet value comparison
  const isFacetValueInArray = useCallback((value: string, array: string[] | undefined): boolean => {
    if (!array || array.length === 0) return false;
    return array.some(v => v.toLowerCase() === value.toLowerCase());
  }, []);

  // Combine all facets - memoize expensive calculation
  const allFacetsRaw = useMemo<FacetItem[]>(() => {
    try {
      const locationItems = facetsData[locationAttribute] || [];
      const remotenessItems = facetsData.remoteness || [];
      const jobTypeItems = facetsData.jobType || [];
      const roleLevelItems = facetsData.normalisedRoleLevel || [];
      const companyItems = facetsData.companyId || [];
      const companyIndustryItems = facetsData.companyIndustry || [];
      const requiredLanguagesItems = facetsData.requiredLanguages || [];
      const benefitsItems = facetsData.benefits || [];
      const skillsAndTechnologiesItems = facetsData.skillsAndTechnologies || [];
      const fieldItems = facetsData.field || [];
      const educationLevelItems = facetsData.educationLevel || [];
      const peopleManagementItems = facetsData.peopleManagementTier || [];

      return [
        // Remoteness filters
        ...remotenessItems.map(item => {
          const excludeKey = 'exclude_remoteness';
          const isActive = isFacetValueInArray(item.value, filtersObj.remoteness);
          const isExcluded = isFacetValueInArray(item.value, filtersObj[excludeKey]);
          return {
            attribute: 'remoteness',
            value: item.value,
            label: item.label,
            count: item.count,
            isActive,
            isExcluded,
            isOnly: false,
            colorClass: getTopFilterColorClass('remoteness', facetMode(isExcluded, false)),
            icon: <Icon name="mapPin" useMaskForColor className="w-3 h-3" />,
          };
        }),
        // Location filters (simplified - full implementation would include location grouping)
        ...locationItems.map(item => {
          const isActive = isFacetValueInArray(item.value, filtersObj[locationAttribute]);
          const isExcluded = isFacetValueInArray(item.value, filtersObj[locationExcludeKey]);
          return {
            attribute: locationAttribute,
            value: item.value,
            label: item.label,
            count: item.count,
            isActive,
            isExcluded,
            isOnly: false,
            colorClass: getTopFilterColorClass(locationAttribute, facetMode(isExcluded, false)),
            icon: <Icon name="mapPin" useMaskForColor className="w-3 h-3" />,
          };
        }),
        // Job Type filters
        ...jobTypeItems.map(item => {
          const excludeKey = 'exclude_jobType';
          const isActive = isFacetValueInArray(item.value, filtersObj.jobType);
          const isExcluded = isFacetValueInArray(item.value, filtersObj[excludeKey]);
          return {
            attribute: 'jobType',
            value: item.value,
            label: item.label,
            count: item.count,
            isActive,
            isExcluded,
            isOnly: false,
            colorClass: getTopFilterColorClass('jobType', facetMode(isExcluded, false)),
            icon: <Icon name="briefcase" useMaskForColor className="w-3 h-3" />,
          };
        }),
        // Role Level filters
        ...roleLevelItems.map(item => {
          const excludeKey = 'exclude_normalisedRoleLevel';
          const isActive = isFacetValueInArray(item.value, filtersObj.normalisedRoleLevel);
          const isExcluded = isFacetValueInArray(item.value, filtersObj[excludeKey]);
          const isOnly = isFacetValueInArray(item.value, filtersObj.only_normalisedRoleLevel);
          return {
            attribute: 'normalisedRoleLevel',
            value: item.value,
            label: item.label,
            count: item.count,
            isActive,
            isExcluded,
            isOnly,
            colorClass: getTopFilterColorClass('normalisedRoleLevel', facetMode(isExcluded, isOnly)),
            icon: <Icon name="chartBar" useMaskForColor className="w-3 h-3" />,
          };
        }),
        // Company Industry filters
        ...companyIndustryItems.map(item => {
          const excludeKey = 'exclude_companyIndustry';
          const isActive = isFacetValueInArray(item.value, filtersObj.companyIndustry);
          const isExcluded = isFacetValueInArray(item.value, filtersObj[excludeKey]);
          const isOnly = isFacetValueInArray(item.value, filtersObj.only_companyIndustry);
          return {
            attribute: 'companyIndustry',
            value: item.value,
            label: item.label,
            count: item.count,
            isActive,
            isExcluded,
            isOnly,
            colorClass: getTopFilterColorClass('companyIndustry', facetMode(isExcluded, isOnly)),
            icon: <Icon name="buildingOffice2" useMaskForColor className="w-3 h-3" />,
          };
        }),
        // Company filters
        ...companyItems.map(item => {
          const excludeKey = 'exclude_companyId';
          const isActive = filtersObj.companyId?.includes(item.value) || false;
          const isExcluded = filtersObj[excludeKey]?.includes(item.value) || false;
          return {
            attribute: 'companyId',
            value: item.value,
            label: item.label,
            count: item.count,
            isActive,
            isExcluded,
            isOnly: false,
            colorClass: getTopFilterColorClass('companyId', facetMode(isExcluded, false)),
            icon: <Icon name="buildingOffice2" useMaskForColor className="w-3 h-3" />,
          };
        }),
        // Required Languages (same order as ALL_TOP_FILTER_ATTRIBUTES_ORDER)
        ...requiredLanguagesItems.map(item => {
          const excludeKey = 'exclude_requiredLanguages';
          const isActive = isFacetValueInArray(item.value, filtersObj.requiredLanguages);
          const isExcluded = isFacetValueInArray(item.value, filtersObj[excludeKey]);
          const isOnly = isFacetValueInArray(item.value, filtersObj.only_requiredLanguages);
          return {
            attribute: 'requiredLanguages',
            value: item.value,
            label: item.label,
            count: item.count,
            isActive,
            isExcluded,
            isOnly,
            colorClass: getTopFilterColorClass('requiredLanguages', facetMode(isExcluded, isOnly)),
            icon: <Icon name="globeAlt" useMaskForColor className="w-3 h-3" />,
          };
        }),
        // Benefits
        ...benefitsItems.map(item => {
          const excludeKey = 'exclude_benefits';
          const isActive = isFacetValueInArray(item.value, filtersObj.benefits);
          const isExcluded = isFacetValueInArray(item.value, filtersObj[excludeKey]);
          const isOnly = isFacetValueInArray(item.value, filtersObj.only_benefits);
          return {
            attribute: 'benefits',
            value: item.value,
            label: item.label,
            count: item.count,
            isActive,
            isExcluded,
            isOnly,
            colorClass: getTopFilterColorClass('benefits', facetMode(isExcluded, isOnly)),
            icon: <Icon name="briefcase" useMaskForColor className="w-3 h-3" />,
          };
        }),
        // Skills and Technologies
        ...skillsAndTechnologiesItems.map(item => {
          const excludeKey = 'exclude_skillsAndTechnologies';
          const isActive = isFacetValueInArray(item.value, filtersObj.skillsAndTechnologies);
          const isExcluded = isFacetValueInArray(item.value, filtersObj[excludeKey]);
          const isOnly = isFacetValueInArray(item.value, filtersObj.only_skillsAndTechnologies);
          return {
            attribute: 'skillsAndTechnologies',
            value: item.value,
            label: item.label,
            count: item.count,
            isActive,
            isExcluded,
            isOnly,
            colorClass: getTopFilterColorClass('skillsAndTechnologies', facetMode(isExcluded, isOnly)),
            icon: <Icon name="briefcase" useMaskForColor className="w-3 h-3" />,
          };
        }),
        // Field
        ...fieldItems.map(item => {
          const excludeKey = 'exclude_field';
          const isActive = isFacetValueInArray(item.value, filtersObj.field);
          const isExcluded = isFacetValueInArray(item.value, filtersObj[excludeKey]);
          const isOnly = isFacetValueInArray(item.value, filtersObj.only_field);
          return {
            attribute: 'field',
            value: item.value,
            label: item.label,
            count: item.count,
            isActive,
            isExcluded,
            isOnly,
            colorClass: getTopFilterColorClass('field', facetMode(isExcluded, isOnly)),
            icon: <Icon name="chartBar" useMaskForColor className="w-3 h-3" />,
          };
        }),
        // Education Level
        ...educationLevelItems.map(item => {
          const excludeKey = 'exclude_educationLevel';
          const isActive = isFacetValueInArray(item.value, filtersObj.educationLevel);
          const isExcluded = isFacetValueInArray(item.value, filtersObj[excludeKey]);
          const isOnly = isFacetValueInArray(item.value, filtersObj.only_educationLevel);
          return {
            attribute: 'educationLevel',
            value: item.value,
            label: item.label,
            count: item.count,
            isActive,
            isExcluded,
            isOnly,
            colorClass: getTopFilterColorClass('educationLevel', facetMode(isExcluded, isOnly)),
            icon: <Icon name="chartBar" useMaskForColor className="w-3 h-3" />,
          };
        }),
        ...peopleManagementItems.map(item => {
          const excludeKey = 'exclude_peopleManagementTier';
          const isActive = isFacetValueInArray(item.value, filtersObj.peopleManagementTier);
          const isExcluded = isFacetValueInArray(item.value, filtersObj[excludeKey]);
          return {
            attribute: 'peopleManagementTier',
            value: item.value,
            label: item.label,
            count: item.count,
            isActive,
            isExcluded,
            isOnly: false,
            colorClass: getTopFilterColorClass('peopleManagementTier', facetMode(isExcluded, false)),
            icon: <Icon name="chartBar" useMaskForColor className="w-3 h-3" />,
          };
        }),
      ];
    } catch (error) {
      return [];
    }
  }, [facetsData, filtersObj, isFacetValueInArray, locationAttribute, locationExcludeKey]);

  // Get all active facet attributes and calculate additional facets
  const additionalFacets = useMemo(() => {
    try {
      const activeFacetAttributes = new Set<string>();
      Object.entries(filtersObj).forEach(([attribute, values]) => {
        if (Array.isArray(values) && values.length > 0) {
          activeFacetAttributes.add(attribute);
        }
      });

      const existingAttributes = new Set(allFacetsRaw.map(f => f.attribute));
      const missingActiveAttributes = Array.from(activeFacetAttributes).filter(
        (attr) => !existingAttributes.has(attr) && !EXCLUDED_FROM_ADDITIONAL_FACETS.has(attr)
      );

      const result: FacetItem[] = [];
      missingActiveAttributes.forEach(attribute => {
        const items = facetsData[attribute] || [];
        const activeValues = filtersObj[attribute] || [];

        activeValues.forEach(value => {
          const facetItem = items.find(item => item.value.toLowerCase() === value.toLowerCase());
          const excludeKey = `exclude_${attribute}`;
          const isExcluded = isFacetValueInArray(value, filtersObj[excludeKey]);
          const isOnly = supportsOnlyFilterMode(attribute) && isFacetValueInArray(value, filtersObj[`only_${attribute}`]);
          if (facetItem) {
            result.push({
              attribute,
              value: facetItem.value,
              label: facetItem.label,
              count: facetItem.count,
              isActive: true,
              isExcluded,
              isOnly,
              colorClass: getTopFilterColorClass(attribute, facetMode(isExcluded, isOnly)),
              icon: <Icon name="briefcase" useMaskForColor className="w-3 h-3" />,
            });
          } else {
            // Pseudo-boolean/single-numeric attributes (isDriversLicenseRequired, salaryRangeMin,
            // etc.) aren't real facets — there's no `facetItem` for them above, and
            // formatFilterLabel's generic capitalize-first-letter fallback would show the raw
            // stored value ("True", "5000") instead of a real label. Use the same message the
            // filter's own toggle/slider shows when this is one of those.
            result.push({
              attribute,
              value,
              label: getKnownSingleValueFilterLabel(attribute, value, formatMessage) ?? formatFilterLabel(attribute, value),
              count: 0,
              isActive: true,
              isExcluded,
              isOnly,
              colorClass: getTopFilterColorClass(attribute, facetMode(isExcluded, isOnly)),
              icon: <Icon name="briefcase" useMaskForColor className="w-3 h-3" />,
            });
          }
        });
      });
      return result;
    } catch (error) {
      return [];
    }
  }, [allFacetsRaw, filtersObj, facetsData, isFacetValueInArray, formatMessage]);

  // Combine predefined facets with additional active facets
  const allFacetsWithActive = useMemo(() => [...allFacetsRaw, ...additionalFacets], [allFacetsRaw, additionalFacets]);

  // Filter out facets that should never appear in the top filter row (e.g. lastSeenMin only lives in the selector)
  const allowedFacets = useMemo(() => allFacetsWithActive.filter((facet) =>
    shouldIncludeFacetInTopFilters(facet.attribute)
  ), [allFacetsWithActive]);

  // Use allowedFacets as-is (unfiltered by query) so the pill list stays stable.
  const filteredFacets = allowedFacets;

  // Separate active and inactive facets
  const activeFacets = useMemo(() => filteredFacets.filter(f => f.isActive || f.isExcluded || f.isOnly), [filteredFacets]);
  const inactiveFacets = useMemo(() => filteredFacets.filter(f => !f.isActive && !f.isExcluded && !f.isOnly), [filteredFacets]);

  // Group active facets by attribute and build final sorted array
  const allFacets = useMemo(() => {
    try {
      // Group active facets by attribute
      const activeFacetsByAttribute = new Map<string, typeof activeFacets>();
      activeFacets.forEach(facet => {
        if (!activeFacetsByAttribute.has(facet.attribute)) {
          activeFacetsByAttribute.set(facet.attribute, []);
        }
        activeFacetsByAttribute.get(facet.attribute)!.push(facet);
      });

      // Sort active facets within each attribute group
      activeFacetsByAttribute.forEach((facets) => {
        if (facets[0]?.attribute === locationAttribute) {
          facets.sort((a, b) => {
            const aGroup = a.locationGroup || '';
            const bGroup = b.locationGroup || '';
            if (aGroup !== bGroup) {
              if (aGroup === 'finland') return -1;
              if (bGroup === 'finland') return 1;
              return aGroup.localeCompare(bGroup);
            }
            return a.label.localeCompare(b.label);
          });
        } else {
          facets.sort((a, b) => a.label.localeCompare(b.label));
        }
      });

      // Build final sorted array: grouped active facets first, then inactive facets
      const result: FacetItem[] = [];
      const attributeOrder = TOP_FILTER_ATTRIBUTE_ORDER;

      // Add active facets grouped by attribute
      attributeOrder.forEach(attr => {
        const facets = activeFacetsByAttribute.get(attr);
        if (facets) {
          result.push(...facets);
        }
      });

      // Add any remaining active facets
      const addedAttributes = new Set<string>(attributeOrder.filter(attr => activeFacetsByAttribute.has(attr)) as string[]);
      activeFacets.forEach(facet => {
        if (!addedAttributes.has(facet.attribute)) {
          result.push(facet);
          addedAttributes.add(facet.attribute);
        }
      });

      // Curated default suggestions (legal review: the old raw top-count-across-every-attribute
      // sort surfaced confusing defaults like "No People Management"). Remote/Hybrid first, then
      // the highest-count locations, then whatever's left sorted by count as before, with
      // peopleManagementTier never suggested (still shows normally if a visitor sets it via
      // Advanced Filters; this only curates what's *offered*, not what can be active).
      const eligibleInactive = inactiveFacets.filter((f) => f.attribute !== 'peopleManagementTier');
      const remotenessPriority = eligibleInactive
        .filter((f) => f.attribute === 'remoteness' && (REMOTE_VALUES.has(f.value.toLowerCase()) || HYBRID_VALUES.has(f.value.toLowerCase())))
        .sort((a, b) => (REMOTE_VALUES.has(a.value.toLowerCase()) ? -1 : REMOTE_VALUES.has(b.value.toLowerCase()) ? 1 : 0));
      const topLocations = eligibleInactive
        .filter((f) => f.attribute === locationAttribute)
        .sort((a, b) => b.count - a.count);
      const prioritized = [...remotenessPriority, ...topLocations];
      const prioritizedKeys = new Set(prioritized.map((f) => `${f.attribute}:${f.value}`));
      const remainingSorted = eligibleInactive
        .filter((f) => !prioritizedKeys.has(`${f.attribute}:${f.value}`))
        .sort((a, b) => b.count - a.count);
      const sortedInactiveFacets = [...prioritized, ...remainingSorted];
      const MAX_FACETS = 20;
      const activeCount = result.length;
      const remainingSlots = Math.max(0, MAX_FACETS - activeCount);
      const limitedInactiveFacets = sortedInactiveFacets.slice(0, remainingSlots);

      result.push(...limitedInactiveFacets);
      return result;
    } catch (error) {
      return [];
    }
  }, [activeFacets, inactiveFacets, locationAttribute]);

  // Build flattened render items for virtualization
  const renderItems: TopFilterRenderItem[] = useMemo(() => {
    try {
      if (allFacets.length === 0) return [];

      const activeFacetsList = allFacets.filter(f => f.isActive || f.isExcluded || f.isOnly);
      const inactiveFacetsList = allFacets.filter(f => !f.isActive && !f.isExcluded && !f.isOnly);

      // Group active facets by attribute
      const activeGroups: Array<{ attribute: string; facets: typeof activeFacetsList; locationGroup?: 'finland' | 'remote' }> = [];
      let currentGroup: typeof activeFacetsList = [];
      let currentAttribute: string | null = null;
      let currentLocationGroup: 'finland' | 'remote' | undefined = undefined;

      activeFacetsList.forEach((facet) => {
        const facetLocationGroup = facet.locationGroup;
        const groupKey = facet.attribute === locationAttribute
          ? `${facet.attribute}_${facetLocationGroup || 'other'}`
          : facet.attribute;
        const prevGroupKey = currentAttribute === locationAttribute
          ? `${currentAttribute}_${currentLocationGroup || 'other'}`
          : currentAttribute;

        if (currentAttribute === null || groupKey !== prevGroupKey) {
          if (currentGroup.length > 0 && currentAttribute !== null) {
            activeGroups.push({
              attribute: currentAttribute,
              facets: [...currentGroup],
              locationGroup: currentLocationGroup,
            });
          }
          currentGroup = [facet];
          currentAttribute = facet.attribute;
          currentLocationGroup = facetLocationGroup;
        } else {
          currentGroup.push(facet);
        }
      });

      if (currentGroup.length > 0 && currentAttribute !== null) {
        activeGroups.push({
          attribute: currentAttribute,
          facets: [...currentGroup],
          locationGroup: currentLocationGroup,
        });
      }

      // Build final array: grouped active facets, then individual inactive facets
      const finalGroups: Array<{ attribute: string; facets: typeof allFacets; isGrouped: boolean; locationGroup?: 'finland' | 'remote' }> = [];

      activeGroups.forEach(group => {
        finalGroups.push({ ...group, isGrouped: group.facets.length > 1 });
      });

      inactiveFacetsList.forEach(facet => {
        finalGroups.push({
          attribute: facet.attribute,
          facets: [facet],
          isGrouped: false,
          locationGroup: facet.locationGroup,
        });
      });

      const items: TopFilterRenderItem[] = [];

      finalGroups.forEach((group, groupIdx) => {
        const nextGroup = finalGroups[groupIdx + 1];
        const isGrouped = group.isGrouped;
        const groupHasActive = group.facets[0].isActive || group.facets[0].isExcluded || group.facets[0].isOnly;
        const nextGroupHasActive = nextGroup && (nextGroup.facets[0].isActive || nextGroup.facets[0].isExcluded || nextGroup.facets[0].isOnly);
        const shouldShowConnectorBetweenGroups = !!(groupHasActive && nextGroupHasActive);

        if (isGrouped && groupHasActive) {
          items.push({ kind: 'bracket', char: '[', key: `group-${group.attribute}-${groupIdx}-open` });
        }

        group.facets.forEach((facet, facetIdx) => {
          const nextFacet = group.facets[facetIdx + 1];
          const shouldShowOr = isGrouped && nextFacet && (facet.isActive || facet.isExcluded || facet.isOnly) && (nextFacet.isActive || nextFacet.isExcluded || nextFacet.isOnly);

          items.push({
            kind: 'facet',
            facet,
            key: `facet-${facet.attribute}-${facet.value}-${groupIdx}-${facetIdx}`,
          });

          if (shouldShowOr) {
            items.push({
              kind: 'connector',
              text: 'OR',
              key: `or-${facet.attribute}-${facet.value}-${groupIdx}-${facetIdx}`,
            });
          }
        });

        if (isGrouped && groupHasActive) {
          items.push({ kind: 'bracket', char: ']', key: `group-${group.attribute}-${groupIdx}-close` });
        }

        if (shouldShowConnectorBetweenGroups) {
          items.push({
            kind: 'connector',
            text: 'AND',
            key: `and-${group.attribute}-${groupIdx}`,
          });
        }
      });

      return items;
    } catch (error) {
      return [];
    }
  }, [allFacets, locationAttribute]);

  const hasClearableFilters = useMemo(
    () => Object.values(filtersObj).some((arr) => Array.isArray(arr) && arr.length > 0),
    [filtersObj],
  );

  const handleClearAllFilters = useCallback(() => {
    pushIndexUiState({
      query: query ?? '',
      refinementList: {},
      page: 0,
      sort,
    });
  }, [pushIndexUiState, query, sort]);

  const handleFacetClick = useCallback((attribute: string, value: string, mode: TopFilterMode = 'include') => {
    try {
      const excludeKey = `exclude_${attribute}`;
      const onlyKey = `only_${attribute}`;
      const currentValues = filtersObj[attribute] || [];
      const excludedValues = filtersObj[excludeKey] || [];
      const onlyValues = filtersObj[onlyKey] || [];
      const isActive = currentValues.some(v => v.toLowerCase() === value.toLowerCase());

      const newFilters = { ...filtersObj };

      if (mode === 'exclude') {
        // Clicking an already-excluded pill clears the exclusion; it must not also
        // fall through to the include branch below or the filter becomes
        // contradictory (attribute:=X && attribute:!=X), silently zeroing results.
        const remaining = excludedValues.filter(v => v.toLowerCase() !== value.toLowerCase());
        if (remaining.length > 0) {
          newFilters[excludeKey] = remaining;
        } else {
          delete newFilters[excludeKey];
        }
      } else if (mode === 'only') {
        // Clicking an already-"only" pill clears it, same as clearing an exclusion above.
        const remaining = onlyValues.filter(v => v.toLowerCase() !== value.toLowerCase());
        if (remaining.length > 0) {
          newFilters[onlyKey] = remaining;
        } else {
          delete newFilters[onlyKey];
        }
      } else if (isActive) {
        const remaining = currentValues.filter(v => v.toLowerCase() !== value.toLowerCase());
        if (remaining.length > 0) {
          newFilters[attribute] = remaining;
        } else {
          delete newFilters[attribute];
        }
      } else {
        const existingIndex = currentValues.findIndex(v => v.toLowerCase() === value.toLowerCase());
        if (existingIndex !== -1) {
          const updated = [...currentValues];
          updated[existingIndex] = value;
          newFilters[attribute] = updated;
        } else {
          newFilters[attribute] = [...currentValues, value];
        }
      }

      const hasAny = Object.keys(newFilters).length > 0;
      pushIndexUiState({
        query: query ?? '',
        refinementList: hasAny ? newFilters : {},
        page: 0,
        sort,
      });
    } catch (error) {
      // Error handling
    }
  }, [filtersObj, pushIndexUiState, query, sort]);

  if (renderItems.length === 0) {
    return null;
  }

  return (
    <aside className={`relative ${className}`} style={{ height: '64px' }} aria-label="Filter categories">
      <div className="h-full flex items-center min-w-0">
        {/* Logo on left of scroll row - small screens only */}
        {clearSearch != null && (
          <div className="flex-shrink-0 pl-3 pr-2 lg:hidden flex items-center">
            <Logo clearSearch={clearSearch} variant="icon" locale={intl.locale} />
          </div>
        )}
        {/* Gradients live on this wrapper (not the aside) so the right fade sits at the scroll
            edge next to the clear button — at the aside edge it landed behind the clear button
            and pills hard-clipped instead of fading (and the left one overlaid the mobile logo). */}
        <div className="relative h-full min-w-0 flex-1 flex items-center">
          <div className="absolute left-0 top-0 bottom-4 w-8 bg-gradient-to-r from-white dark:from-[#1F1509] to-transparent pointer-events-none z-10" />
          <div className="absolute right-0 top-0 bottom-4 w-8 bg-gradient-to-l from-white dark:from-[#1F1509] to-transparent pointer-events-none z-10" />
        <div
          ref={listContainerRef}
          className="flex h-full min-h-0 min-w-0 w-full items-center overflow-x-auto overflow-y-hidden pl-2 pr-1 lg:pl-2 lg:pr-1 xl:pl-20 xl:pr-1"
          style={{
            scrollbarWidth: 'thin',
            scrollbarColor: 'rgba(156, 163, 175, 0.5) transparent',
            ...dragToScroll.style,
          }}
          onMouseDown={dragToScroll.onMouseDown}
          onMouseLeave={dragToScroll.onMouseLeave}
        >
          <div className="flex w-max min-w-0 flex-nowrap items-center gap-1">
          {renderItems.map((item) => {
            if (item.kind === 'bracket') {
              return (
                <div
                  key={item.key}
                  style={{ flex: '0 0 auto', display: 'flex', alignItems: 'center' }}
                >
                  <span className="text-[#7A6E5F] dark:text-[#8A7060] text-lg font-light select-none">
                    {item.char}
                  </span>
                </div>
              );
            }

            if (item.kind === 'connector') {
              const text =
                item.text === 'OR'
                  ? formatMessage({ id: 'filters.connector.or', defaultMessage: 'OR', description: 'Filter connector label for OR logic' })
                  : formatMessage({ id: 'filters.connector.and', defaultMessage: 'AND', description: 'Filter connector label for AND logic' });

              return (
                <div
                  key={item.key}
                  style={{ flex: '0 0 auto', display: 'flex', alignItems: 'center' }}
                >
                  <span className="px-0.5 text-xs font-semibold text-[#5A4A3A] dark:text-[#B8A48C] select-none">
                    {text}
                  </span>
                </div>
              );
            }

            const facet = item.facet;

            return (
              <div
                key={item.key}
                style={{ flex: '0 0 auto', display: 'flex', alignItems: 'center' }}
              >
                <button
                  className={`flex items-center gap-1.5 rounded-full px-4 py-2 font-medium text-sm transition-all duration-200 cursor-pointer whitespace-nowrap border-2 ${
                    facet.isExcluded
                      ? 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 border-red-300 dark:border-red-700 shadow-sm dark:shadow-none hover:brightness-[0.97] dark:hover:brightness-110'
                      : facet.isOnly
                      ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border-blue-300 dark:border-blue-700 shadow-sm dark:shadow-none hover:brightness-[0.97] dark:hover:brightness-110'
                      : facet.isActive
                      ? `${facet.colorClass} shadow-sm dark:shadow-none hover:brightness-[0.97] dark:hover:brightness-110`
                      : 'bg-[#F2EADF] dark:bg-[#2A1D0F] text-[#3D3529] dark:text-[#C4A882] border-transparent hover:bg-[#EDE3D4] dark:hover:bg-[#342415]'
                  }`}
                  onClick={() => handleFacetClick(facet.attribute, facet.value, facetMode(facet.isExcluded, facet.isOnly))}
                >
                  {facet.icon}
                  {facet.isExcluded && (
                    <span className="text-red-500 dark:text-red-400 font-bold">✗</span>
                  )}
                  {facet.isOnly && (
                    <span className="text-blue-500 dark:text-blue-400 font-bold">=</span>
                  )}
                  {facet.attribute === 'companyId' ? (
                    <CompanyName name={facet.label} />
                  ) : (
                    <span>{facet.label}</span>
                  )}
                  <span className="text-xs opacity-75">({facet.count})</span>
                </button>
              </div>
            );
          })}
          </div>
        </div>
        </div>
        {hasClearableFilters ? (
          <div className="flex-shrink-0 z-20 flex items-center justify-center border-l border-[#DDD5C8]/60 bg-white pl-2 pr-3 dark:border-[#4A3520]/60 dark:bg-[#1F1509] xl:pr-20">
            <button
              type="button"
              className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-[#DDD5C8] text-[#5A4A3A] transition-colors hover:bg-[#F2EADF] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#3B5E45]/40 dark:border-[#4A3520] dark:text-[#B8A48C] dark:hover:bg-[#342415] dark:focus-visible:ring-[#98CFA8]/40"
              onClick={handleClearAllFilters}
              aria-label={formatMessage({
                id: 'filters.top.clearAll',
                defaultMessage: 'Clear all filters',
                description: 'Aria label for control that removes every active filter on the top filter row',
              })}
            >
              <Icon name="xMark" useMaskForColor className="w-4 h-4" aria-hidden />
            </button>
          </div>
        ) : null}
      </div>
    </aside>
  );
};
